<!-- What goes with the link. THIS FILE IS YOURS.

     Sharing a kitchen has always been one tap and a URL, and the URL has always BEEN the
     kitchen: deflated into the fragment, never sent to a server, nothing stored anywhere to
     leak or to delete later. That is the default here and it is still the default -- open
     this sheet, press Share, and that is exactly what happens.

     The reason there is a sheet at all is that the design is not the only thing worth
     sending. The room as the LiDAR found it, the photographs somebody brought, the 3D scan
     file itself: a maker quoting this kitchen wants all three beside it, and not one of them
     fits in a URL at any compression. So each is a box, and ticking any of them changes what
     the link IS -- from a kitchen you carry to a package on a server that anyone with the
     link can open.

     That is a real trade and this sheet's whole job is to make it legible before it is made,
     which is why the line under the button changes with the boxes and says the true thing
     each time rather than a general reassurance. -->
<template>
  <div class="scrim" @click.self="$emit('close')">
    <section class="card" role="dialog" aria-modal="true" aria-labelledby="share-title">
      <header>
        <h1 id="share-title">Share this kitchen</h1>
        <button class="x" type="button" aria-label="Back to the kitchen" @click="$emit('close')">
          <IonIcon :icon="closeOutline" />
        </button>
      </header>

      <p class="lede">Pick what goes with it.</p>

      <ul class="boxes">
        <li>
          <label>
            <input type="checkbox" checked disabled />
            <span class="text">
              <span class="k">The design</span>
              <span class="b">The kitchen as you have it now — walls, cabinets, finishes.</span>
            </span>
          </label>
        </li>

        <li v-if="original">
          <label>
            <input type="checkbox" v-model="wantOriginal" :disabled="!!busy" />
            <span class="text">
              <span class="k">The room as it was scanned</span>
              <span class="b">
                What the LiDAR measured before any of this design — the walls, what was
                plumbed and where. So {{ makerName }} can see the two side by side.
              </span>
            </span>
          </label>
        </li>

        <li v-if="photoCount">
          <label>
            <input type="checkbox" v-model="wantPhotos" :disabled="!!busy" />
            <span class="text">
              <span class="k">Your photos</span>
              <span class="b">
                {{ photoCount }} picture{{ photoCount === 1 ? '' : 's' }}, {{ photoSize }} —
                the plans, room photos and renders you brought.
              </span>
            </span>
          </label>
        </li>

        <li v-if="scanModel && native">
          <label>
            <input type="checkbox" v-model="wantRoom" :disabled="!!busy" />
            <span class="text">
              <span class="k">The 3D scan file</span>
              <span class="b">
                The room itself, as a model anyone can spin and measure. It is a large file
                and it is uploaded, which takes a moment on mobile data.
              </span>
            </span>
          </label>
        </li>
      </ul>

      <p class="what" :class="{ hosted }">{{ what }}</p>

      <button class="go" type="button" :disabled="!!busy" @click="send">
        {{ busy ? 'Working…' : hosted ? 'Upload and share' : 'Share the link' }}
      </button>
      <p v-if="progress" class="progress" role="status">{{ progress }}</p>

      <p class="fine">
        {{ hosted
          ? 'Anyone with the link can open it, so send it to people you mean to. It stops working after 90 days.'
          : 'Anyone with the link can open it. Nothing is stored anywhere, so it works for as long as they keep it.' }}
      </p>
    </section>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { IonIcon } from '@ionic/vue';
import { closeOutline } from 'ionicons/icons';
import { shareUrl } from '../kitchen/store.js';
import { shareLink, isNative } from '../kitchen/share.js';
import { packageFor, uploadShare } from '../kitchen/hosted.js';

const props = defineProps({
  plan: Object,
  maker: Object,
  /** The room as it was scanned, or null when this session did not scan one. */
  original: { type: Object, default: null },
  /** A device-local file:// path to the scan's USDZ, or null. Native only, never shared as a path. */
  scanModel: { type: String, default: null },
});
const emit = defineEmits(['close', 'said']);

const wantOriginal = ref(false);
const wantPhotos = ref(false);
const wantRoom = ref(false);
const busy = ref(false);
const progress = ref('');

// The 3D scan box only exists on a phone: the file is written by RoomPlan and lives in the
// app's own container, so on the web there is nothing behind the checkbox to send.
const native = isNative();
const makerName = computed(() => props.maker?.name ?? 'your maker');

/*
 * THE PICTURES, COUNTED THE WAY THEY WILL BE SENT.
 *
 * A photo can be on the design and on the Original both -- the same photograph of the same
 * room, attached twice -- and `packageFor` sends one copy. Counting them any other way here
 * would show a number and a size that the upload then disagrees with.
 */
const photos = computed(() => {
  const seen = new Set();
  const out = [];
  for (const i of [...(props.plan?.images ?? []), ...(props.original?.images ?? [])]) {
    const id = i?.id ?? i?.dataUrl?.slice(-40);
    if (!i?.dataUrl || seen.has(id)) continue;
    seen.add(id);
    out.push(i);
  }
  return out;
});
const photoCount = computed(() => photos.value.length);
const photoSize = computed(() => {
  const kb = photos.value.reduce((n, i) => n + (i.bytes ?? 0), 0) / 1024;
  return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.round(kb)} KB`;
});

/** Anything past the design means there is something to upload. */
const hosted = computed(() => (wantOriginal.value && !!props.original)
  || (wantPhotos.value && photoCount.value > 0)
  || (wantRoom.value && !!props.scanModel));

const what = computed(() => {
  if (!hosted.value) return 'Nothing leaves your phone but this link. The design travels inside the link itself.';
  const bits = ['the design'];
  if (wantOriginal.value && props.original) bits.push('the room as scanned');
  if (wantPhotos.value && photoCount.value) bits.push(`${photoCount.value} photo${photoCount.value === 1 ? '' : 's'}`);
  if (wantRoom.value && props.scanModel) bits.push('the 3D scan');
  const list = bits.length > 1 ? `${bits.slice(0, -1).join(', ')} and ${bits[bits.length - 1]}` : bits[0];
  return `This uploads ${list} to the server, and the link points at them.`;
});

async function send() {
  busy.value = true;
  progress.value = '';
  try {
    if (!hosted.value) return await sendLink();
    return await sendPackage();
  } catch (e) {
    emit('said', `Could not share it: ${e?.message ?? e}`);
  } finally {
    busy.value = false;
    progress.value = '';
  }
}

/** The old path, unchanged: the whole design in the fragment, and no server involved. */
async function sendLink() {
  const url = await shareUrl(props.plan, props.maker);
  const r = await shareLink(url, { title: 'My kitchen', text: `A kitchen I designed with ${makerName.value}` });
  emit('said', {
    shared: 'Sent. Nothing left your phone but the link.',
    copied: 'Link copied. The kitchen is inside the link itself — nothing was uploaded.',
    failed: 'Could not copy the link.',
  }[r] ?? null);
  if (r !== 'failed') emit('close');
}

async function sendPackage() {
  const pkg = packageFor(props.plan, props.original, {
    includeOriginal: wantOriginal.value,
    includePhotos: wantPhotos.value,
    includeRoom: wantRoom.value && !!props.scanModel,
  });
  const { url } = await uploadShare(pkg, {
    roomPath: props.scanModel,
    onProgress: (line) => { progress.value = line; },
  });
  progress.value = '';
  const r = await shareLink(url, { title: 'My kitchen', text: `A kitchen I designed with ${makerName.value}` });
  emit('said', {
    shared: 'Sent. Anyone with that link can open it for the next 90 days.',
    copied: 'Link copied. Anyone with it can open the kitchen for the next 90 days.',
    failed: 'It uploaded, but the link could not be copied.',
  }[r] ?? null);
  if (r !== 'failed') emit('close');
}
</script>

<style scoped>
.scrim {
  position: fixed; inset: 0; z-index: 60;
  background: color-mix(in oklab, var(--k-ink) 46%, transparent);
  display: flex; align-items: flex-end; justify-content: center;
}
@media (min-width: 700px) { .scrim { align-items: center; padding: 24px; } }
.card {
  width: 100%; max-width: 460px; max-height: 92vh; overflow-y: auto;
  background: var(--k-paper); border-radius: 20px 20px 0 0;
  padding: 6px 20px calc(20px + env(safe-area-inset-bottom));
}
@media (min-width: 700px) { .card { border-radius: 16px; } }

header { display: flex; align-items: center; gap: 10px; padding: 12px 0 4px; }
h1 { flex: 1; margin: 0; font: 600 20px/1.25 var(--ion-font-family, system-ui); color: var(--k-ink); letter-spacing: -.015em; }
.x { border: 0; background: none; color: var(--k-mute); cursor: pointer; width: 40px; height: 40px; display: grid; place-items: center; font-size: 20px; }
.lede { margin: 0 0 14px; font: 400 14px/1.5 var(--ion-font-family, system-ui); color: var(--k-mute); }

.boxes { margin: 0 0 14px; padding: 0; list-style: none; display: grid; gap: 2px; }
.boxes label {
  display: flex; align-items: flex-start; gap: 11px; cursor: pointer;
  padding: 11px 12px; border-radius: 11px; background: var(--k-fill);
}
.boxes input {
  width: 19px; height: 19px; margin: 1px 0 0; flex: none;
  accent-color: var(--k-ink); cursor: pointer;
}
.boxes input:disabled { cursor: default; }
.text { display: grid; gap: 3px; }
.k { font: 600 13.5px/1.3 var(--ion-font-family, system-ui); color: var(--k-ink); }
.b { font: 400 12px/1.45 var(--ion-font-family, system-ui); color: var(--k-mute); }

/* The one line that changes with the boxes. It is the sentence the decision turns on, so it
   is not fine print -- it sits above the button at the size of something meant to be read. */
.what {
  margin: 0 0 12px; padding: 10px 12px; border-radius: 10px;
  border: 1px solid var(--k-line);
  font: 400 12.5px/1.45 var(--ion-font-family, system-ui); color: var(--k-dim);
}
.what.hosted { border-color: var(--k-ink); color: var(--k-ink); }

.go {
  width: 100%; border: 0; border-radius: 12px; cursor: pointer;
  background: var(--k-ink); color: var(--k-paper);
  font: 600 15px/1 var(--ion-font-family, system-ui);
  padding: 16px; min-height: 54px;
}
.go:disabled { opacity: .55; }
.progress { margin: 9px 0 0; text-align: center; font: 500 12.5px/1.4 var(--ion-font-family, system-ui); color: var(--k-dim); }
.fine { margin: 12px 0 0; font: 400 11.5px/1.45 var(--ion-font-family, system-ui); color: var(--k-mute); }
</style>
